/**
 * Display formatters for the analytics dashboard. Locale-aware via Intl, with
 * "credits" as the unit for cost (Virtual Brain bills usage in credits).
 */

import { differenceInCalendarDays, format, parseISO, subDays } from "date-fns";

/** The YYYY-MM-DD key `days` before `dateKey`. */
export function subtractDays(dateKey: string, days: number): string {
	return format(subDays(parseISO(dateKey), days), "yyyy-MM-dd");
}

/** A YYYY-MM-DD key → "Mar 9, 2026" (UTC). */
function formatDay(key: string): string {
	return new Date(`${key}T00:00:00Z`).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC",
	});
}

/** An inclusive from/to window → "Mar 9, 2026 – Jun 1, 2026". */
export function formatDateRange(from: string, to: string): string {
	return `${formatDay(from)} – ${formatDay(to)}`;
}

/** The equal-length period immediately before `[from, to]` (inclusive), as YYYY-MM-DD. */
export function previousPeriod(from: string, to: string): { from: string; to: string } {
	const windowDays = differenceInCalendarDays(parseISO(to), parseISO(from)) + 1;

	return {
		from: subtractDays(from, windowDays),
		to: subtractDays(from, 1),
	};
}

/** A raw timeline key (YYYY-MM-DD) → axis label, formatted by the chosen granularity. */
export function formatTimelineLabel(key: string, granularity: "day" | "week" | "month"): string {
	const date = new Date(`${key}T00:00:00Z`);
	const options: Intl.DateTimeFormatOptions =
		granularity === "month"
			? { month: "short", year: "numeric", timeZone: "UTC" }
			: { month: "short", day: "numeric", timeZone: "UTC" };

	return date.toLocaleString("en-US", options);
}

const numberFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const costFormatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

/** 12345 → "12,345". Counts only; money goes through `formatCost`. */
export function formatNumber(value: number): string {
	return numberFormatter.format(value);
}

/** `part` as a whole-number percentage of `whole` (0 when `whole` is 0). */
export function percentOf(part: number, whole: number): number {
	return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/** 45.7166 → "45.72". The single money formatter: every cost goes through it. */
export function formatCost(value: number): string {
	return costFormatter.format(value);
}

/** 4.637 → "4.64 credits". */
export function formatCredits(value: number): string {
	return `${formatCost(value)} credits`;
}

export type Delta = {
	/** Signed percentage change vs the prior period (0 if prior was 0). */
	readonly percentChange: number;
	readonly direction: "up" | "down" | "flat";
};

/** Percentage change of `current` against `previous`. */
export function computeDelta(current: number, previous: number): Delta {
	if (previous === 0) {
		return { percentChange: current === 0 ? 0 : 100, direction: current === 0 ? "flat" : "up" };
	}

	const percentChange = ((current - previous) / Math.abs(previous)) * 100;
	let direction: Delta["direction"] = "flat";

	if (percentChange > 0.05) direction = "up";
	else if (percentChange < -0.05) direction = "down";

	return { percentChange, direction };
}

/** Format an ISO timestamp as a short, human-friendly date, or a dash. */
export function formatLastActive(iso: string | null): string {
	if (!iso) return "-";

	return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date(iso));
}

const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
	dateStyle: "medium",
	timeStyle: "short",
});

/** ISO timestamp → "Jul 20, 2026, 3:44 PM" in the viewer's own timezone. */
export function formatDateTime(iso: string | null): string {
	if (!iso) return "-";

	return dateTimeFormatter.format(new Date(iso));
}

/** A value formatter chosen by metric kind, for KPI cards and tooltips. */
export type MetricKind = "count" | "credits";

export function formatMetric(value: number, kind: MetricKind): string {
	switch (kind) {
		case "credits":
			return formatCredits(value);
		case "count":
			return formatNumber(value);
	}
}
